import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Professional PDF Export Engine with Clara360 Branding
 * Generates high-quality property exposés with intelligent layouts
 */
class PDFExportEngine {
  constructor() {
    this.brandColors = {
      primary: '#2563eb',    // Clara360 Blue
      secondary: '#7c3aed',  // Clara360 Purple
      accent: '#059669',     // Success Green
      text: '#1f2937',       // Dark Gray
      lightGray: '#f3f4f6',  // Light Background
      white: '#ffffff'
    };

    this.templates = {
      modern_apartment: {
        name: 'Modern Apartment',
        layout: 'single_column',
        colors: ['primary', 'secondary'],
        suitable: ['Mehrfamilienhaus', 'Einfamilienhaus']
      },
      commercial_property: {
        name: 'Commercial Property',
        layout: 'two_column',
        colors: ['primary', 'accent'],
        suitable: ['Gewerbeimmobilie', 'Büro']
      },
      luxury_estate: {
        name: 'Luxury Estate',
        layout: 'magazine',
        colors: ['secondary', 'text'],
        suitable: ['Einfamilienhaus', 'Villa']
      }
    };
  }

  /**
   * Generate professional PDF exposé
   */
  async generateExpose(exposeData) {
    try {
      const { property, unit, pricing, description, documents, template = 'modern_apartment' } = exposeData;
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      let currentY = 20;

      // Page 1: Cover Page
      currentY = await this.generateCoverPage(pdf, {
        property,
        unit,
        pricing,
        description,
        template,
        pageWidth,
        pageHeight
      });

      // Page 2: Details & Photos
      pdf.addPage();
      currentY = 20;
      currentY = await this.generateDetailsPage(pdf, {
        property,
        unit,
        pricing,
        documents,
        template,
        pageWidth,
        pageHeight,
        startY: currentY
      });

      // Page 3: Location & Contact
      pdf.addPage();
      currentY = 20;
      await this.generateLocationPage(pdf, {
        property,
        template,
        pageWidth,
        pageHeight,
        startY: currentY
      });

      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('PDF-Generierung fehlgeschlagen: ' + error.message);
    }
  }

  /**
   * Generate cover page with branding
   */
  async generateCoverPage(pdf, { property, unit, pricing, description, template, pageWidth, pageHeight }) {
    let currentY = 20;

    // Clara360 Header with Logo
    await this.addHeader(pdf, pageWidth, currentY);
    currentY += 30;

    // Property Type Badge
    const propertyTypeConfig = this.getPropertyTypeConfig(property?.property_type);
    pdf.setFillColor(...this.hexToRgb(propertyTypeConfig.color));
    pdf.roundedRect(20, currentY, 60, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(propertyTypeConfig.emoji + ' ' + (property?.property_type || 'Property'), 25, currentY + 5.5);
    currentY += 20;

    // Main Title
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    const title = description?.title || `${unit?.rooms || 'N/A'}-Zimmer-Wohnung`;
    pdf.text(title, 20, currentY, { maxWidth: pageWidth - 40 });
    currentY += 15;

    // Location
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    const location = `${property?.address || 'Adresse'}, ${property?.city || 'Stadt'}`;
    pdf.text('📍 ' + location, 20, currentY);
    currentY += 20;

    // Key Facts Box
    const boxHeight = 60;
    pdf.setFillColor(...this.hexToRgb(this.brandColors.lightGray));
    pdf.roundedRect(20, currentY, pageWidth - 40, boxHeight, 3, 3, 'F');
    
    // Key Facts Content
    const facts = [
      { label: 'Zimmer', value: unit?.rooms || 'N/A', icon: '🏠' },
      { label: 'Wohnfläche', value: `${unit?.size_sqm || 'N/A'}m²`, icon: '📐' },
      { label: 'Miete', value: `€${pricing?.adjustedRent || 'N/A'}`, icon: '💰' },
      { label: 'Nebenkosten', value: `€${pricing?.utilities || 'N/A'}`, icon: '📋' }
    ];

    const factBoxWidth = (pageWidth - 60) / 2;
    facts.forEach((fact, index) => {
      const x = 30 + (index % 2) * factBoxWidth;
      const y = currentY + 15 + Math.floor(index / 2) * 20;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
      pdf.text(fact.icon + ' ' + fact.label, x, y);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
      pdf.text(fact.value, x + 40, y);
    });
    
    currentY += boxHeight + 20;

    // Description Preview
    if (description?.description) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
      
      const descriptionLines = pdf.splitTextToSize(description.description, pageWidth - 40);
      const previewLines = descriptionLines.slice(0, 8); // First 8 lines
      
      pdf.text(previewLines, 20, currentY);
      currentY += previewLines.length * 5 + 10;
    }

    // Pricing Highlight Box
    const pricingBoxHeight = 40;
    pdf.setFillColor(...this.hexToRgb(this.brandColors.primary));
    pdf.roundedRect(20, pageHeight - 80, pageWidth - 40, pricingBoxHeight, 3, 3, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Gesamtmiete: €' + ((pricing?.adjustedRent || 0) + (pricing?.utilities || 0)), 30, pageHeight - 55);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`zzgl. €${pricing?.deposit || 'N/A'} Kaution`, 30, pageHeight - 45);

    // Footer
    await this.addFooter(pdf, pageWidth, pageHeight);

    return currentY;
  }

  /**
   * Generate details page with photos and specifications
   */
  async generateDetailsPage(pdf, { property, unit, pricing, documents, template, pageWidth, pageHeight, startY }) {
    let currentY = startY;

    // Page Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.text('Objektdetails & Ausstattung', 20, currentY);
    currentY += 15;

    // Photos Section (if available)
    if (documents?.photos && documents.photos.length > 0) {
      currentY = await this.addPhotosSection(pdf, documents.photos, pageWidth, currentY);
      currentY += 10;
    }

    // Property Details Table
    const details = [
      { label: 'Objekttyp', value: property?.property_type || 'N/A' },
      { label: 'Zimmeranzahl', value: unit?.rooms || 'N/A' },
      { label: 'Wohnfläche', value: `${unit?.size_sqm || 'N/A'} m²` },
      { label: 'Baujahr', value: property?.construction_year || 'N/A' },
      { label: 'Etage', value: unit?.floor || 'N/A' },
      { label: 'Verfügbar ab', value: unit?.available_from || 'Sofort' },
      { label: 'Kaltmiete', value: `€${pricing?.adjustedRent || 'N/A'}` },
      { label: 'Nebenkosten', value: `€${pricing?.utilities || 'N/A'}` },
      { label: 'Kaution', value: `€${pricing?.deposit || 'N/A'}` },
      { label: 'Preis/m²', value: `€${pricing?.pricePerSqm || 'N/A'}` }
    ];

    currentY = this.addDetailsTable(pdf, details, pageWidth, currentY);
    currentY += 15;

    // Equipment & Features
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.text('Ausstattung & Besonderheiten', 20, currentY);
    currentY += 10;

    const features = this.generateFeaturesList(property, unit);
    currentY = this.addFeaturesList(pdf, features, pageWidth, currentY);

    // Energy Certificate (if available)
    if (documents?.energyCertificate) {
      currentY += 15;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Energieausweis', 20, currentY);
      currentY += 10;
      
      // Add energy certificate info
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Energieausweis verfügbar - Details auf Anfrage', 20, currentY);
      currentY += 10;
    }

    return currentY;
  }

  /**
   * Generate location page with map and contact
   */
  async generateLocationPage(pdf, { property, template, pageWidth, pageHeight, startY }) {
    let currentY = startY;

    // Location Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.text('Lage & Infrastruktur', 20, currentY);
    currentY += 15;

    // Address Box
    pdf.setFillColor(...this.hexToRgb(this.brandColors.lightGray));
    pdf.roundedRect(20, currentY, pageWidth - 40, 25, 3, 3, 'F');
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.text('📍 Adresse', 30, currentY + 8);
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${property?.address || 'Adresse'}, ${property?.postal_code || ''} ${property?.city || 'Stadt'}`, 30, currentY + 18);
    
    currentY += 35;

    // Location Highlights
    const locationHighlights = [
      '🚇 Öffentliche Verkehrsmittel in der Nähe',
      '🛒 Einkaufsmöglichkeiten fußläufig erreichbar',
      '🏥 Ärzte und Apotheken in der Umgebung',
      '🎓 Schulen und Kindergärten vorhanden',
      '🌳 Parks und Grünflächen in der Nähe',
      '🚗 Gute Verkehrsanbindung'
    ];

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Infrastruktur & Umgebung', 20, currentY);
    currentY += 10;

    locationHighlights.forEach(highlight => {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(highlight, 20, currentY);
      currentY += 6;
    });

    currentY += 20;

    // Contact Section
    pdf.setFillColor(...this.hexToRgb(this.brandColors.primary));
    pdf.roundedRect(20, currentY, pageWidth - 40, 60, 3, 3, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Kontakt & Besichtigung', 30, currentY + 15);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Clara360 Immobilienverwaltung', 30, currentY + 25);
    pdf.text('📞 +49 (0) 681 123 456', 30, currentY + 35);
    pdf.text('✉️ info@clara360.de', 30, currentY + 45);
    pdf.text('🌐 www.clara360.de', 30, currentY + 55);

    // QR Code placeholder
    pdf.setFillColor(255, 255, 255);
    pdf.rect(pageWidth - 70, currentY + 10, 40, 40, 'F');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.setFontSize(8);
    pdf.text('QR-Code für\nOnline-Details', pageWidth - 68, currentY + 32);

    return currentY + 70;
  }

  /**
   * Add Clara360 header with branding
   */
  async addHeader(pdf, pageWidth, y) {
    // Clara360 Logo placeholder
    pdf.setFillColor(...this.hexToRgb(this.brandColors.primary));
    pdf.rect(20, y, 8, 8, 'F');
    
    // Company Name
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Clara360', 32, y + 6);
    
    // Tagline
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Intelligente Immobilienverwaltung', 32, y + 14);

    // Date
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.setFontSize(10);
    pdf.text(new Date().toLocaleDateString('de-DE'), pageWidth - 40, y + 6);
  }

  /**
   * Add footer with branding
   */
  async addFooter(pdf, pageWidth, pageHeight) {
    const footerY = pageHeight - 15;
    
    pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    
    // Left side
    pdf.text('Erstellt mit Clara360 Marketplace', 20, footerY);
    
    // Right side
    pdf.text('www.clara360.de', pageWidth - 40, footerY);
    
    // Center line
    pdf.setDrawColor(...this.hexToRgb(this.brandColors.lightGray));
    pdf.line(20, footerY - 5, pageWidth - 20, footerY - 5);
  }

  /**
   * Add photos section
   */
  async addPhotosSection(pdf, photos, pageWidth, startY) {
    let currentY = startY;
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.text('Objektfotos', 20, currentY);
    currentY += 10;

    // Photo placeholders (3 per row)
    const photoWidth = (pageWidth - 60) / 3;
    const photoHeight = photoWidth * 0.75;
    
    for (let i = 0; i < Math.min(photos.length, 6); i++) {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const x = 20 + col * (photoWidth + 10);
      const y = currentY + row * (photoHeight + 10);
      
      // Photo placeholder
      pdf.setFillColor(...this.hexToRgb(this.brandColors.lightGray));
      pdf.rect(x, y, photoWidth, photoHeight, 'F');
      
      // Photo label
      pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
      pdf.setFontSize(8);
      pdf.text(`Foto ${i + 1}`, x + 2, y + photoHeight - 2);
    }
    
    currentY += Math.ceil(Math.min(photos.length, 6) / 3) * (photoHeight + 10);
    return currentY;
  }

  /**
   * Add details table
   */
  addDetailsTable(pdf, details, pageWidth, startY) {
    let currentY = startY;
    const tableWidth = pageWidth - 40;
    const rowHeight = 8;
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...this.hexToRgb(this.brandColors.primary));
    pdf.text('Objektdaten', 20, currentY);
    currentY += 10;

    details.forEach((detail, index) => {
      const y = currentY + index * rowHeight;
      
      // Alternating row colors
      if (index % 2 === 0) {
        pdf.setFillColor(...this.hexToRgb(this.brandColors.lightGray));
        pdf.rect(20, y - 2, tableWidth, rowHeight, 'F');
      }
      
      // Label
      pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(detail.label, 25, y + 3);
      
      // Value
      pdf.setFont('helvetica', 'bold');
      pdf.text(detail.value, pageWidth / 2, y + 3);
    });
    
    return currentY + details.length * rowHeight;
  }

  /**
   * Add features list
   */
  addFeaturesList(pdf, features, pageWidth, startY) {
    let currentY = startY;
    const colWidth = (pageWidth - 60) / 2;
    
    features.forEach((feature, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = 20 + col * (colWidth + 20);
      const y = currentY + row * 6;
      
      pdf.setTextColor(...this.hexToRgb(this.brandColors.text));
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('✓ ' + feature, x, y);
    });
    
    return currentY + Math.ceil(features.length / 2) * 6;
  }

  /**
   * Generate features list based on property data
   */
  generateFeaturesList(property, unit) {
    const features = [];
    
    // Standard features
    if (unit?.rooms) features.push(`${unit.rooms} Zimmer`);
    if (unit?.size_sqm) features.push(`${unit.size_sqm}m² Wohnfläche`);
    if (unit?.balcony) features.push('Balkon vorhanden');
    if (unit?.garden) features.push('Garten vorhanden');
    if (unit?.parking) features.push('Stellplatz vorhanden');
    if (property?.elevator) features.push('Aufzug vorhanden');
    if (unit?.fitted_kitchen) features.push('Einbauküche');
    if (unit?.basement) features.push('Kellerraum');
    
    // Default features if no specific data
    if (features.length === 0) {
      features.push(
        'Moderne Ausstattung',
        'Gute Verkehrsanbindung',
        'Zentrale Lage',
        'Gepflegtes Objekt'
      );
    }
    
    return features;
  }

  /**
   * Get property type configuration
   */
  getPropertyTypeConfig(propertyType) {
    const configs = {
      'Mehrfamilienhaus': { emoji: '🏢', color: this.brandColors.primary },
      'Gewerbeimmobilie': { emoji: '🏪', color: this.brandColors.accent },
      'Einfamilienhaus': { emoji: '🏠', color: '#f59e0b' },
      'Büro': { emoji: '💼', color: this.brandColors.secondary },
      'Lager': { emoji: '🏭', color: '#6b7280' }
    };
    
    return configs[propertyType] || configs['Mehrfamilienhaus'];
  }

  /**
   * Convert hex color to RGB array
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  }

  /**
   * Export PDF with filename
   */
  async exportPDF(exposeData, filename) {
    try {
      const pdf = await this.generateExpose(exposeData);
      const finalFilename = filename || this.generateFilename(exposeData);
      
      pdf.save(finalFilename);
      return { success: true, filename: finalFilename };
    } catch (error) {
      console.error('PDF export error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate smart filename
   */
  generateFilename(exposeData) {
    const { property, unit } = exposeData;
    const city = property?.city || 'Stadt';
    const address = property?.address?.split(' ')[0] || 'Objekt';
    const unitId = unit?.unit_identifier || unit?.id?.slice(0, 8) || 'Unit';
    const date = new Date().toISOString().split('T')[0];
    
    return `Clara360_Expose_${city}_${address}_${unitId}_${date}.pdf`;
  }

  /**
   * Get available templates for property type
   */
  getAvailableTemplates(propertyType) {
    return Object.entries(this.templates)
      .filter(([key, template]) => template.suitable.includes(propertyType))
      .map(([key, template]) => ({ key, ...template }));
  }
}

export default PDFExportEngine;

